/**
 * Service report — client-side aggregation of platform-wide metrics for the
 * admin dashboard. Reuses existing (admin-gated) GraphQL queries so no new
 * backend resolver or deploy is required:
 *   - GET_USERS_ADMIN  → user totals + signups (today / this month)
 *   - getCampaigns({}) → campaigns by status / visit country
 *   - getMatches({})   → applications by status + applied-since trends
 *   - getInfluencers() → influencer profiles, SNS-linked, by main category
 *
 * Also produces a self-contained HTML report (Chart.js via CDN) for download,
 * and a print-to-PDF helper that opens the same HTML in a new window.
 */

import { gql } from '@apollo/client';
import apolloClient from '../graphql/apollo-client';
import { graphqlApi } from '../services/graphql';
import { GET_USERS_ADMIN } from '../graphql/queries';

/**
 * Minimal influencer projection for the report. Deliberately avoids
 * non-nullable list fields like `portfolio` that can be null in older
 * records — requesting them makes AppSync reject the whole response with
 * "Cannot return null for non-nullable type".
 */
const GET_INFLUENCERS_REPORT = gql`
  query GetInfluencersReport($filters: InfluencerFiltersInput) {
    getInfluencers(filters: $filters) {
      influencers {
        influencerId
        mainCategory
        createdAt
        socialMediaHandles {
          instagram { account }
          youtube { account }
          tiktok { account }
          twitter { account }
          naverblog { account }
        }
      }
      total
    }
  }
`;

export interface ServiceReport {
  generatedAt: string;
  users: {
    total: number;
    byType: Record<string, number>;
    newToday: number;
    newThisMonth: number;
  };
  influencers: {
    profiles: number;
    withSnsLinked: number;
    byMainCategory: Record<string, number>;
  };
  campaigns: {
    total: number;
    byStatus: Record<string, number>;
    byVisitCountry: Record<string, number>;
  };
  matches: {
    total: number;
    byStatus: Record<string, number>;
    appliedToday: number;
    applied7d: number;
    applied30d: number;
    appliedThisMonth: number;
  };
}

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

function boundaries() {
  const now = new Date();
  const nowKst = new Date(now.getTime() + KST_OFFSET_MS);
  const dayStart = new Date(
    Date.UTC(nowKst.getUTCFullYear(), nowKst.getUTCMonth(), nowKst.getUTCDate()) - KST_OFFSET_MS,
  ).toISOString();
  const monthStart = new Date(
    Date.UTC(nowKst.getUTCFullYear(), nowKst.getUTCMonth(), 1) - KST_OFFSET_MS,
  ).toISOString();
  const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const thirtyStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
  return { dayStart, monthStart, weekStart, thirtyStart };
}

const tally = (rows: any[], field: string): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const r of rows) {
    const key = String((r?.[field] ?? '(none)') || '(none)');
    out[key] = (out[key] || 0) + 1;
  }
  return out;
};

const countSince = (rows: any[], since: string, field = 'createdAt') =>
  rows.filter((r) => (r?.[field] || '') >= since).length;

function hasSnsLinked(inf: any): boolean {
  const h = inf?.socialMediaHandles;
  if (!h || typeof h !== 'object') return false;
  return Object.values(h).some((v: any) => v?.account && String(v.account).trim());
}

/** Fetch all source data in parallel and assemble the report. */
export async function buildServiceReport(): Promise<ServiceReport> {
  const { dayStart, monthStart, weekStart, thirtyStart } = boundaries();

  const [usersRes, campRes, matchRes, infRes] = await Promise.all([
    apolloClient
      .query({
        query: gql`${GET_USERS_ADMIN}`,
        variables: { filters: { page: 1, pageSize: 1 } },
        fetchPolicy: 'network-only',
      })
      .catch(() => null),
    graphqlApi.getCampaigns({}).catch(() => null),
    graphqlApi.getMatches({}).catch(() => null),
    apolloClient
      .query({
        query: GET_INFLUENCERS_REPORT,
        variables: { filters: {} },
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      })
      .catch(() => null),
  ]);

  const stats = usersRes?.data?.getUsersAdmin?.stats;
  const campaigns: any[] = campRes?.success ? campRes.data?.campaigns || [] : [];
  const matches: any[] = matchRes?.success ? matchRes.data?.matches || [] : [];
  const influencers: any[] = infRes?.data?.getInfluencers?.influencers || [];

  return {
    generatedAt: new Date().toISOString(),
    users: {
      total: stats?.total ?? 0,
      byType: {
        influencer: stats?.influencer ?? 0,
        brand: stats?.brand ?? 0,
        agency: stats?.agency ?? 0,
      },
      newToday: stats?.dailyNew ?? 0,
      newThisMonth: stats?.monthlyNew ?? 0,
    },
    influencers: {
      // Use the users-table influencer count so this matches the
      // "users by type" chart. The influencers-table records are still used
      // below for SNS-linked / category breakdowns.
      profiles: stats?.influencer ?? influencers.length,
      withSnsLinked: influencers.filter(hasSnsLinked).length,
      byMainCategory: tally(influencers, 'mainCategory'),
    },
    campaigns: {
      total: campaigns.length,
      byStatus: tally(campaigns, 'status'),
      byVisitCountry: tally(campaigns, 'visitCountry'),
    },
    matches: {
      total: matches.length,
      byStatus: tally(matches, 'status'),
      appliedToday: countSince(matches, dayStart, 'appliedAt'),
      applied7d: countSince(matches, weekStart, 'appliedAt'),
      applied30d: countSince(matches, thirtyStart, 'appliedAt'),
      appliedThisMonth: countSince(matches, monthStart, 'appliedAt'),
    },
  };
}

/** Sort a tally object into [key, count] pairs, descending by count. */
export const ranked = (obj: Record<string, number>): [string, number][] =>
  Object.entries(obj).sort((a, b) => b[1] - a[1]);

const COUNTRY_NAMES: Record<string, string> = {
  vn: 'Vietnam', th: 'Thailand', jp: 'Japan', id: 'Indonesia', gu: 'Guam',
  us: 'USA', kr: 'Korea', mo: 'Macao', cn: 'China', my: 'Malaysia',
  ph: 'Philippines', tw: 'Taiwan', hk: 'Hong Kong', sg: 'Singapore',
  '(none)': 'Unspecified',
};
export const countryLabel = (code: string) => COUNTRY_NAMES[code] || code;

export const REPORT_PALETTE = [
  '#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7',
  '#ec4899', '#14b8a6', '#f97316', '#3b82f6', '#84cc16', '#eab308',
];

/** Build a self-contained HTML report (Chart.js from CDN). */
export function buildReportHtml(report: ServiceReport): string {
  const usersByType = ranked(report.users.byType);
  const campByStatus = ranked(report.campaigns.byStatus);
  const campByCountry = ranked(report.campaigns.byVisitCountry);
  const matchByStatus = ranked(report.matches.byStatus);

  const charts = {
    usersByType: { labels: usersByType.map(([k]) => k), data: usersByType.map(([, v]) => v) },
    signups: {
      labels: ['Today', 'This month'],
      data: [report.users.newToday, report.users.newThisMonth],
    },
    sns: {
      labels: ['SNS linked', 'No SNS'],
      data: [
        report.influencers.withSnsLinked,
        Math.max(0, report.influencers.profiles - report.influencers.withSnsLinked),
      ],
    },
    campByStatus: { labels: campByStatus.map(([k]) => k), data: campByStatus.map(([, v]) => v) },
    campByCountry: {
      labels: campByCountry.map(([k]) => countryLabel(k)),
      data: campByCountry.map(([, v]) => v),
    },
    matchByStatus: { labels: matchByStatus.map(([k]) => k), data: matchByStatus.map(([, v]) => v) },
  };

  const generatedLabel = new Date(report.generatedAt).toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
  const stat = (label: string, value: number | string, sub?: string) => `
    <div class="stat"><div class="stat-value">${value}</div>
    <div class="stat-label">${label}</div>${sub ? `<div class="stat-sub">${sub}</div>` : ''}</div>`;

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8" />
<title>Matchgo Service Report</title>
<script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js"></script>
<style>
*{box-sizing:border-box;}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Apple SD Gothic Neo',sans-serif;margin:0;color:#1f2937;background:#fff;}
.page{padding:32px 40px;}
header{border-bottom:3px solid #6366f1;padding-bottom:16px;margin-bottom:24px;}
h1{font-size:24px;margin:0 0 4px;color:#111827;} .meta{color:#6b7280;font-size:12px;}
h2{font-size:15px;text-transform:uppercase;letter-spacing:.04em;color:#6366f1;margin:28px 0 12px;border-bottom:1px solid #e5e7eb;padding-bottom:6px;}
.stats{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:8px;}
.stat{flex:1;min-width:110px;background:#f9fafb;border:1px solid #eef2f7;border-radius:10px;padding:12px 14px;}
.stat-value{font-size:22px;font-weight:700;color:#111827;} .stat-label{font-size:11px;color:#6b7280;margin-top:2px;}
.stat-sub{font-size:10px;color:#9ca3af;margin-top:2px;}
.grid{display:flex;flex-wrap:wrap;gap:24px;}
.card{flex:1;min-width:280px;border:1px solid #eef2f7;border-radius:12px;padding:16px;}
.card h3{margin:0 0 12px;font-size:13px;color:#374151;}
.chart-wrap{position:relative;height:240px;}
footer{margin-top:32px;padding-top:12px;border-top:1px solid #e5e7eb;color:#9ca3af;font-size:11px;}
@media print{.card,.stat{break-inside:avoid;}h2{break-after:avoid;}}
</style></head>
<body><div class="page">
<header><h1>Matchgo — Overall Service Report</h1>
<div class="meta">Generated ${generatedLabel}</div></header>
<h2>Key metrics</h2>
<div class="stats">
${stat('Total users', report.users.total)}
${stat('Influencers', report.influencers.profiles)}
${stat('Campaigns', report.campaigns.total)}
${stat('Applications', report.matches.total)}
${stat('New users today', report.users.newToday, 'KST')}
${stat('Applied today', report.matches.appliedToday, 'KST')}
</div>
<h2>Users</h2><div class="grid">
<div class="card"><h3>By type</h3><div class="chart-wrap"><canvas id="usersByType"></canvas></div></div>
<div class="card"><h3>New signups</h3><div class="chart-wrap"><canvas id="signups"></canvas></div></div>
</div>
<h2>Influencers</h2><div class="grid">
<div class="card"><h3>SNS linked vs not</h3><div class="chart-wrap"><canvas id="sns"></canvas></div></div>
</div>
<h2>Campaigns</h2><div class="grid">
<div class="card"><h3>By status</h3><div class="chart-wrap"><canvas id="campByStatus"></canvas></div></div>
<div class="card"><h3>By visit country</h3><div class="chart-wrap"><canvas id="campByCountry"></canvas></div></div>
</div>
<h2>Matches / Applications</h2><div class="grid">
<div class="card"><h3>By status (funnel)</h3><div class="chart-wrap"><canvas id="matchByStatus"></canvas></div></div>
</div>
<footer>Read-only snapshot assembled from production data at generation time.</footer>
</div>
<script>
const PALETTE=${JSON.stringify(REPORT_PALETTE)};const CHARTS=${JSON.stringify(charts)};
Chart.defaults.animation=false;
Chart.defaults.font.family="-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif";
const doughnut=(id,cfg)=>new Chart(document.getElementById(id),{type:'doughnut',
data:{labels:cfg.labels,datasets:[{data:cfg.data,backgroundColor:PALETTE}]},
options:{responsive:true,maintainAspectRatio:false,plugins:{legend:{position:'right',labels:{boxWidth:12,font:{size:11}}}}}});
const bar=(id,cfg,color)=>new Chart(document.getElementById(id),{type:'bar',
data:{labels:cfg.labels,datasets:[{data:cfg.data,backgroundColor:color||PALETTE}]},
options:{responsive:true,maintainAspectRatio:false,indexAxis:'y',plugins:{legend:{display:false}},scales:{x:{beginAtZero:true,ticks:{precision:0}}}}});
doughnut('usersByType',CHARTS.usersByType);
bar('signups',CHARTS.signups,'#6366f1');
doughnut('sns',CHARTS.sns);
doughnut('campByStatus',CHARTS.campByStatus);
bar('campByCountry',CHARTS.campByCountry,'#06b6d4');
bar('matchByStatus',CHARTS.matchByStatus,'#22c55e');
</script></body></html>`;
}

/** Download the report as a standalone .html file. */
export function downloadReportHtml(report: ServiceReport): void {
  const blob = new Blob([buildReportHtml(report)], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const ts = report.generatedAt.replace(/[:.]/g, '-').slice(0, 19);
  a.href = url;
  a.download = `matchgo-service-report-${ts}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * "Download PDF" — open the report HTML in a new window and trigger the print
 * dialog, where the admin chooses "Save as PDF". This keeps the Chart.js
 * graphs intact without pulling in a heavyweight html-to-canvas dependency.
 */
export function downloadReportPdf(report: ServiceReport): void {
  const win = window.open('', '_blank');
  if (!win) {
    // Popup blocked — fall back to HTML download.
    downloadReportHtml(report);
    return;
  }
  win.document.open();
  win.document.write(buildReportHtml(report));
  win.document.close();
  // Give Chart.js time to load from CDN and render before invoking print.
  const triggerPrint = () => {
    setTimeout(() => {
      win.focus();
      win.print();
    }, 1200);
  };
  if (win.document.readyState === 'complete') triggerPrint();
  else win.onload = triggerPrint;
}
